import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGift,
  faPlus,
  faMagnifyingGlass,
  faXmark,
  faTriangleExclamation,
  faRotate,
  faTrashCan,
  faUserPlus,
  faArrowRight,
  faCircleCheck,
  faTrophy,
} from '@fortawesome/free-solid-svg-icons';
import './Referrals.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const STATUS = {
  booked: { label: 'Free class booked', tone: 'is-info' },
  trial: { label: 'Free class planned', tone: 'is-info' },
  joined: { label: 'Joined · waiting for payment', tone: 'is-good' },
  paid: { label: 'Discount ready for sharer', tone: 'is-gold' },
  rewarded: { label: 'Sharer used the discount', tone: 'is-off' },
};
const FILTERS = [
  ['all', 'All', () => true],
  ['trial', 'Free class', (r) => r.Status === 'booked' || r.Status === 'trial'],
  ['joined', 'Joined', (r) => r.Status === 'joined'],
  ['paid', 'Discount ready', (r) => r.Status === 'paid'],
  ['rewarded', 'Used', (r) => r.Status === 'rewarded'],
];
const isJoined = (r) => ['joined', 'paid', 'rewarded'].includes(r.Status);
const fmtDay = (v) => {
  const d = v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
};

/* Search a student by name (for "Add referral") */
const PersonPicker = ({ label, hint, value, onPick, onlyPaid, disabled }) => {
  const [q, setQ] = useState('');
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (value || q.trim().length < 2) {
      setList([]);
      return undefined;
    }
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const res = await axios.get(`${API}/api/admin/referrals/people?q=${encodeURIComponent(q.trim())}`, auth());
        setList((res.data || []).filter((p) => !onlyPaid || p.paid));
      } catch {
        setList([]);
      } finally {
        setBusy(false);
      }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q, value, onlyPaid]);

  return (
    <div className="rmarf-field">
      <span>{label}</span>
      {value ? (
        <div className="rmarf-picked">
          <b>{value.name}</b>
          <em>{value.paid ? 'Enrolled' : 'Demo'}</em>
          <button type="button" onClick={() => { onPick(null); setQ(''); }} disabled={disabled} aria-label={`Change ${label}`}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      ) : (
        <>
          <label className="rmarf-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a name…" aria-label={label} disabled={disabled} />
          </label>
          {busy && <small>Searching…</small>}
          {!busy && q.trim().length >= 2 && list.length === 0 && <small>No match{onlyPaid ? ' among enrolled students' : ''}.</small>}
          {list.length > 0 && (
            <ul className="rmarf-results">
              {list.map((p) => (
                <li key={p.id}>
                  <button type="button" onClick={() => onPick(p)}>
                    <b>{p.name}</b>
                    <em>{p.paid ? 'Enrolled' : 'Demo'}</em>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {hint && <small>{hint}</small>}
    </div>
  );
};

const Referrals = () => {
  const [rows, setRows] = useState([]);
  const [rules, setRules] = useState({ step: 10, cap: 30 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [toast, setToast] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [sharer, setSharer] = useState(null);
  const [friend, setFriend] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState('');

  const [removing, setRemoving] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    axios
      .get(`${API}/api/admin/referrals`, auth())
      .then((res) => {
        if (cancelled) return;
        setRows(res.data?.referrals || []);
        setRules({ step: res.data?.step || 10, cap: res.data?.cap || 30 });
      })
      .catch((err) => !cancelled && setError(err.response?.data?.error || 'We couldn’t load the referrals.'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [reload]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!addOpen && !removing) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (!saving) setAddOpen(false);
      if (!removeBusy) setRemoving(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [addOpen, removing, saving, removeBusy]);

  const counts = useMemo(() => Object.fromEntries(FILTERS.map(([k, , fn]) => [k, rows.filter(fn).length])), [rows]);
  const shown = useMemo(() => {
    const fn = FILTERS.find(([k]) => k === filter)[2];
    const q = query.trim().toLowerCase();
    return rows.filter(fn).filter((r) => !q || `${r.FriendName || ''} ${r.ReferrerName || ''} ${r.ReferralCode || ''}`.toLowerCase().includes(q));
  }, [rows, filter, query]);

  // families who brought the most friends that joined
  const top = useMemo(() => {
    const m = new Map();
    rows.filter(isJoined).forEach((r) => {
      const k = String(r.ReferrerId);
      if (!m.has(k)) m.set(k, { id: r.ReferrerId, name: r.ReferrerName, n: 0 });
      m.get(k).n += 1;
    });
    return [...m.values()].sort((a, b) => b.n - a.n).slice(0, 5);
  }, [rows]);

  const openAdd = () => {
    setSharer(null);
    setFriend(null);
    setAddError('');
    setAddOpen(true);
  };

  const saveAdd = async () => {
    setSaving(true);
    setAddError('');
    try {
      await axios.post(`${API}/api/admin/referrals`, { referrerId: sharer.id, friendId: friend.id }, auth());
      setAddOpen(false);
      setToast('Referral added.');
      setReload((n) => n + 1);
    } catch (err) {
      setAddError(err.response?.data?.error || 'Couldn’t add the referral.');
    } finally {
      setSaving(false);
    }
  };

  const confirmRemove = async () => {
    setRemoveBusy(true);
    setRemoveError('');
    try {
      await axios.delete(`${API}/api/admin/referrals/${removing.Id}`, auth());
      setRemoving(null);
      setToast('Referral removed.');
      setReload((n) => n + 1);
    } catch (err) {
      setRemoveError(err.response?.data?.error || 'Couldn’t remove it.');
    } finally {
      setRemoveBusy(false);
    }
  };

  if (loading && !rows.length) {
    return (
      <div className="rmarf" aria-busy="true">
        <div className="rmarf-skel rmarf-skel--head" />
        <div className="rmarf-skel" />
        <div className="rmarf-skel" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rmarf">
        <div className="rmarf-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{error}</p>
          <button type="button" className="rmarf-btn" onClick={() => setReload((n) => n + 1)}>
            <FontAwesomeIcon icon={faRotate} /> Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rmarf">
      <header className="rmarf-header">
        <div>
          <h1>Referrals</h1>
          <p>
            Friends invited by your families. The friend gets {rules.step}% off their first package; the family who shared gets {rules.step}% per friend who joins and
            pays, up to {rules.cap}% on one package.
          </p>
        </div>
        <button type="button" className="rmarf-btn" onClick={openAdd}>
          <FontAwesomeIcon icon={faPlus} /> Add referral
        </button>
      </header>

      <ul className="rmarf-stats">
        <li>
          <b>{rows.length}</b> invited
        </li>
        <li className="is-info">
          <b>{counts.trial}</b> free class
        </li>
        <li className="is-good">
          <b>{rows.filter(isJoined).length}</b> joined
        </li>
        <li className="is-gold">
          <b>{counts.paid}</b> discounts ready
        </li>
        <li className="is-off">
          <b>{counts.rewarded}</b> used
        </li>
      </ul>

      {top.length > 0 && (
        <div className="rmarf-top">
          <span>
            <FontAwesomeIcon icon={faTrophy} /> Top families
          </span>
          {top.map((t) => (
            <a key={t.id} href={`/admin/StudentProfile/${t.id}`} target="_blank" rel="noopener noreferrer">
              {t.name} <b>{t.n}</b>
            </a>
          ))}
        </div>
      )}

      <div className="rmarf-tools">
        <div className="rmarf-tabs" role="tablist" aria-label="Show">
          {FILTERS.map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={filter === k} className={filter === k ? 'is-on' : ''} onClick={() => setFilter(k)}>
              {label} <span>{counts[k]}</span>
            </button>
          ))}
        </div>
        <label className="rmarf-search">
          <FontAwesomeIcon icon={faMagnifyingGlass} />
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search friend, family or code" aria-label="Search" />
        </label>
      </div>

      {shown.length === 0 ? (
        <div className="rmarf-empty">
          <FontAwesomeIcon icon={rows.length ? faMagnifyingGlass : faGift} />
          <p>
            {rows.length
              ? 'No referrals match.'
              : 'No referrals yet. Students see an “Invite a friend” card on their dashboard with their personal link.'}
          </p>
        </div>
      ) : (
        <ul className="rmarf-list">
          {shown.map((r) => {
            const st = STATUS[r.Status] || STATUS.booked;
            return (
              <li key={r.Id} className={st.tone}>
                <div className="rmarf-flow">
                  <a className="rmarf-who" href={`/admin/StudentProfile/${r.ReferrerId}?tab=awaitingpayment`} target="_blank" rel="noopener noreferrer">
                    <small>Shared by</small>
                    <b>{r.ReferrerName || 'Unknown'}</b>
                    {r.ReferralCode && <code>{r.ReferralCode}</code>}
                  </a>
                  <FontAwesomeIcon icon={faArrowRight} className="rmarf-arrow" aria-hidden="true" />
                  {r.FriendProfileId ? (
                    <a className="rmarf-who" href={`/admin/StudentProfile/${r.FriendProfileId}`} target="_blank" rel="noopener noreferrer">
                      <small>Friend</small>
                      <b>{r.FriendName || 'Friend'}</b>
                    </a>
                  ) : (
                    <span className="rmarf-who">
                      <small>Friend</small>
                      <b>{r.FriendName || 'Friend'}</b>
                    </span>
                  )}
                </div>
                <div className="rmarf-meta">
                  <span className="rmarf-pill">{st.label}</span>
                  {isJoined(r) && (
                    <span className={`rmarf-tag ${r.FriendPaymentId ? 'is-done' : ''}`}>
                      {r.FriendPaymentId ? <FontAwesomeIcon icon={faCircleCheck} /> : null} Friend {rules.step}%: {r.FriendPaymentId ? 'used' : 'not used yet'}
                    </span>
                  )}
                  <span className="rmarf-date">
                    {fmtDay(r.CreatedAt)}
                    {r.Source === 'manual' ? ' · added by hand' : ' · link'}
                  </span>
                </div>
                {!r.FriendPaymentId && !r.ReferrerPaymentId && (
                  <button type="button" className="rmarf-del" onClick={() => { setRemoveError(''); setRemoving(r); }} aria-label="Remove referral" title="Remove">
                    <FontAwesomeIcon icon={faTrashCan} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* ---------- Add referral ---------- */}
      {addOpen && (
        <div className="rmarf-overlay" onClick={() => !saving && setAddOpen(false)}>
          <div className="rmarf-modal" role="dialog" aria-modal="true" aria-label="Add referral" onClick={(e) => e.stopPropagation()}>
            <header>
              <h2>
                <FontAwesomeIcon icon={faUserPlus} /> Add referral
              </h2>
              <button type="button" className="rmarf-x" onClick={() => setAddOpen(false)} disabled={saving} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </header>
            <div className="rmarf-modal-body">
              <p className="rmarf-lead">For a family that told you on WhatsApp who sent them, instead of using the link.</p>
              <PersonPicker label="Who shared" hint="An enrolled student" value={sharer} onPick={setSharer} onlyPaid disabled={saving} />
              <PersonPicker label="Who joined (the friend)" hint="Demo or enrolled student" value={friend} onPick={setFriend} disabled={saving} />
              {addError && <p className="rmarf-error">{addError}</p>}
            </div>
            <footer>
              <button type="button" className="rmarf-btn rmarf-btn--ghost" onClick={() => setAddOpen(false)} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="rmarf-btn" onClick={saveAdd} disabled={saving || !sharer || !friend}>
                {saving ? 'Saving…' : 'Add referral'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* ---------- Remove ---------- */}
      {removing && (
        <div className="rmarf-overlay" onClick={() => !removeBusy && setRemoving(null)}>
          <div className="rmarf-modal rmarf-modal--small" role="alertdialog" aria-modal="true" aria-label="Remove referral" onClick={(e) => e.stopPropagation()}>
            <div className="rmarf-modal-body">
              <h2>Remove this referral?</h2>
              <p className="rmarf-lead">
                {removing.ReferrerName} → {removing.FriendName}. No discount will be given for it.
              </p>
              {removeError && <p className="rmarf-error">{removeError}</p>}
            </div>
            <footer>
              <button type="button" className="rmarf-btn rmarf-btn--ghost" onClick={() => setRemoving(null)} disabled={removeBusy} autoFocus>
                Cancel
              </button>
              <button type="button" className="rmarf-btn rmarf-btn--danger" onClick={confirmRemove} disabled={removeBusy}>
                {removeBusy ? 'Removing…' : 'Remove'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {toast && (
        <div className="rmarf-toast" role="status">
          <FontAwesomeIcon icon={faCircleCheck} /> {toast}
        </div>
      )}
    </div>
  );
};

export default Referrals;
